/**
 * The `?tab=` search param on the vehicle record (`$vehicleId.tsx`).
 * Operation is first and the default (PRD: "licence plate, registration,
 * location, cost centre and mileage are what a fleet manager works with
 * daily, while the technical data is looked up"). `masterData` carries the
 * eleven-section catalogue as cards; `driver` is a gated shell here (content
 * lands in issue 12).
 */
export type VehicleDetailTab = 'operation' | 'masterData' | 'driver';
export const VEHICLE_DETAIL_TABS = ['operation', 'masterData', 'driver'] as const satisfies readonly VehicleDetailTab[];

/**
 * Falls back to Operation for a deep link naming a tab that either doesn't
 * exist in v1 or that this principal holds no readable category for — a tab
 * disappears when its categories are all unreadable (PRD permissions rule),
 * so its content can never render for a `Tabs` root with no matching trigger.
 */
export function resolveVehicleDetailTab(
  requestedTab: VehicleDetailTab | undefined,
  visibleTabs: readonly VehicleDetailTab[],
): VehicleDetailTab {
  const fallback = visibleTabs[0] ?? 'operation';
  if (requestedTab !== undefined && visibleTabs.includes(requestedTab)) {
    return requestedTab;
  }
  return fallback;
}

/** Default tab is omitted from the URL, mirroring the driver record's `withDriverDetailTab`. */
export function withVehicleDetailTab<T extends { tab?: VehicleDetailTab }>(prev: T, next: VehicleDetailTab): T {
  return { ...prev, tab: next === 'operation' ? undefined : next };
}
